import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ChartSubType,
  GridDashboardTextItem,
  HistoricalUtrs,
  InsightDashboardItemType,
  TextSubType,
  TableSubType,
  DisplayMode,
} from '../../types/insight-custom-dashboard';
import { ChartWidget } from './items/charts/ChartWidget';
import { TextWidget } from '@features/custom-dashboard';
import { navigateToQuestionFromUtrv } from './question-reference/navigateToQuestion';
import {
  getFilteredUtrsData,
  isChartType,
  isDisaggregatedRendering,
  isMediaType,
  isSDGContributionType,
  isSDGTrackerType,
  isSingleUtrChart,
  isSpaceType,
  isTableType,
  isTextOrHeadlineType,
} from './utils';
import { MultiUtrsChartWidget } from './items/charts/multi-utrs-chart/MultiUtrsChartWidget';
import { getCurrencyConfig } from './items/charts/multi-utrs-chart/utils';
import { QuestionReference } from './question-reference/QuestionReference';
import { TableWidget } from '@features/custom-dashboard/items/tables/TableWidget';
import { DirectTableWidget } from '@features/custom-dashboard/items/tables/DirectTableWidget';
import { MultiUtrsChartFooter } from './items/charts/common/MultiUtrsChartFooter';
import { NoData } from './items/charts/common/NoData';
import { MediaWidget } from '@features/custom-dashboard/items/MediaWidget';
import { SDGContributionWidget } from '@features/custom-dashboard/items/SDGContributionWidget';
import { SingleValueIntegration } from '@routes/custom-dashboard/items/charts/multi-utrs-chart/SingleValueIntegration';
import { UniversalTrackerModalShowAs } from '@g17eco/types/universalTrackerModal';
import { getLatestMetricText } from '@features/custom-dashboard/utils/dashboard-utils';
import { RichTextEditorContainer, buildVariableMap } from '@features/rich-text-editor';
import { DisaggregatedChartWidget } from '@features/custom-dashboard/items/disagregated-data-chart/DisaggregatedChartWidget';
import type { DashboardCommonContext } from '@g17eco/types/dashboard';
import { GridItemWrapper } from './GridItemWrapper';
import type { GridItemProps } from './GridItem';

interface TextWidgetWithVariablesProps extends DashboardCommonContext {
  item: Pick<GridDashboardTextItem, 'type' | 'subType' | 'text' | 'displayMode' | 'title'> & {
    editorState?: GridDashboardTextItem['editorState'];
  };
  isEditing?: boolean;
  effectiveDate?: string;
  period?: string;
  onViewSource?: () => void;
  questionReference?: React.ReactNode;
}

const TextWidgetWithVariables = ({
  item,
  isEditing,
  dataFilters,
  initiative,
  rootOrg,
  effectiveDate,
  period,
  onViewSource,
  questionReference,
}: TextWidgetWithVariablesProps) => {
  const variableMap = useMemo(
    () =>
      buildVariableMap({
        currentSubsidiaryName: initiative?.name,
        rootSubsidiaryName: rootOrg?.name,
        dataFilters: dataFilters ?? { period: undefined, dateRange: undefined, timeRange: undefined },
      }),
    [initiative?.name, rootOrg?.name, dataFilters]
  );

  return (
    <TextWidget
      item={item}
      isEditing={isEditing}
      variableMap={variableMap}
      effectiveDate={effectiveDate}
      period={period}
      onViewSource={onViewSource}
      questionReference={questionReference}
    />
  );
};

export const GetWidget = (props: GridItemProps) => {
  const {
    item,
    utrsData,
    integrationsData,
    isEditing,
    handleOpenUtrvHistoryModal,
    actionBtn: ActionBtn,
    NoDataView,
    onEditItem,
    gridItemContext,
  } = props;

  const history = useHistory();

  // Destructure from context
  const {
    readOnly,
    hideQuestionReference: hideQuestionReferenceProp,
    initiativeId,
    initiativeIds,
    survey,
    scorecard,
    dataFilters,
    initiative,
    rootOrg,
    valueListMap,
    surveyUnitConfigMap,
  } = gridItemContext;

  const isChart = isChartType(item);
  const isSDGTracker = isSDGTrackerType(item);
  const hideQuestionReference = hideQuestionReferenceProp || item.config?.hideQuestionReference;

  if (isTextOrHeadlineType(item)) {
    const isMetric = item.subType === TextSubType.Metric;
    const metricData: { text: string; effectiveDate?: string; period?: string } =
      isMetric ? getLatestMetricText({ item, utrsData }) : { text: item.text ?? '' };
    const isWidgetMode = item.subType === TextSubType.Metric && item.displayMode === DisplayMode.Widget;

    // Get filtered UTRs data for metric items
    const { variables = {} } = item;
    const { filteredUtrsData, variableUtrCodes } = isMetric
      ? getFilteredUtrsData(utrsData as HistoricalUtrs[], variables)
      : { filteredUtrsData: [], variableUtrCodes: [] };
    const metricTitle = item.title || filteredUtrsData[0]?.utr?.name;

    // Helper function to create QuestionReference component
    const getQuestionReference = (options?: { btnClass?: string; tooltip?: string; name?: string }) =>
      !hideQuestionReference ? (
        <QuestionReference
          initiativeId={initiativeId}
          survey={survey}
          utrsCodes={variableUtrCodes}
          utrsData={filteredUtrsData}
          btnClass={options?.btnClass}
          tooltip={options?.tooltip}
          getName={options?.name ? () => options.name! : undefined}
        />
      ) : null;

    // Widget mode: wrap with GridItemWrapper and add footer
    if (isWidgetMode) {
      const noData = filteredUtrsData.every(({ utrvs }) => !utrvs.length);
      const actionButton = ActionBtn ? <ActionBtn item={item} /> : null;
      const questionReference = getQuestionReference({ btnClass: 'text-left' });

      if (noData) {
        return (
          <GridItemWrapper isEditing={false} item={{ ...item, title: metricTitle }}>
            {NoDataView ? (
              <NoDataView item={item} {...dataFilters} questionReference={questionReference} />
            ) : (
              <NoData
                item={item}
                {...dataFilters}
                actionButton={actionButton}
                questionReference={questionReference}
              />
            )}
          </GridItemWrapper>
        );
      }

      // Create navigation handler for viewing source metric
      const handleViewSource = () => {
        const utrData = filteredUtrsData[0];
        const latestUtrv = utrData?.utrvs?.[0];

        if (!latestUtrv) {
          return;
        }

        navigateToQuestionFromUtrv({ initiativeId, utrv: latestUtrv, history });
      };
      return (
        <RichTextEditorContainer>
          <GridItemWrapper isEditing={false} item={{ ...item, title: metricTitle }}>
            <TextWidgetWithVariables
              item={{ ...item, text: metricData.text, title: metricTitle }}
              isEditing={isEditing}
              dataFilters={dataFilters}
              initiative={initiative}
              rootOrg={rootOrg}
              effectiveDate={metricData.effectiveDate}
              period={metricData.period}
              onViewSource={handleViewSource}
            />
            <MultiUtrsChartFooter item={item} actionButton={actionButton} questionReference={questionReference} />
          </GridItemWrapper>
        </RichTextEditorContainer>
      );
    }

    // Plain text mode: existing behavior
    const metricCode = variableUtrCodes[0] || '';
    const questionTitle = `${metricTitle} (${metricCode})`;
    return (
      <RichTextEditorContainer>
        <TextWidgetWithVariables
          item={{ ...item, text: metricData.text }}
          isEditing={isEditing}
          dataFilters={dataFilters}
          initiative={initiative}
          rootOrg={rootOrg}
          questionReference={getQuestionReference({
            btnClass: 'text-muted p-0 w-100',
            tooltip: questionTitle,
            name: `"${questionTitle}"`
          })}
        />
      </RichTextEditorContainer>
    );
  }

  if (isChart || isSDGTracker) {
    const { variables, calculation } = item;
    // TODO: Have a typeguard or function to ensure that utrsData is dependent on chart type
    // Must be using historical data if we are dealing with non-integration utrs
    const { filteredUtrsData, variableUtrCodes } = getFilteredUtrsData(utrsData as HistoricalUtrs[], variables);
    const noData = filteredUtrsData.every(({ utrvs }) => !utrvs.length);
    const altUnitText = isSDGTracker ? 'contribution' : '';
    const currencyConfig = getCurrencyConfig({ utrsData: filteredUtrsData, surveyUnitConfigMap, variables });

    const actionButton = ActionBtn ? <ActionBtn item={item} /> : null;
    const handleSetTarget = !isEditing
      ? () =>
          handleOpenUtrvHistoryModal({
            item,
            utrData: filteredUtrsData[0],
            activeTab: UniversalTrackerModalShowAs.TargetBaseline,
          })
      : undefined;
    const isDisaggregated = isDisaggregatedRendering({ initiativeIds });

    const getQuestionReference = (className: string = '') =>
      !hideQuestionReference && !isSDGTracker && !isDisaggregated ? (
        <QuestionReference
          initiativeId={initiativeId}
          survey={survey}
          utrsCodes={variableUtrCodes}
          utrsData={filteredUtrsData}
          btnClass={className}
        />
      ) : null;

    if (noData) {
      return (
        <GridItemWrapper isEditing={false} item={item}>
          {NoDataView ? (
            <NoDataView item={item} {...dataFilters} questionReference={getQuestionReference()} />
          ) : (
            <NoData
              item={item}
              {...dataFilters}
              actionButton={actionButton}
              questionReference={getQuestionReference()}
            />
          )}
        </GridItemWrapper>
      );
    }

    if (isDisaggregated) {
      return (
        <GridItemWrapper openHistoryModal={undefined} isEditing={false} item={item}>
          <DisaggregatedChartWidget item={item} utrsData={filteredUtrsData} onEditItem={onEditItem} currencyConfig={currencyConfig} />
        </GridItemWrapper>
      );
    }

    if (calculation) {
      const openHistoryModal = isSingleUtrChart(item.subType, calculation)
        ? () => handleOpenUtrvHistoryModal({ item, utrData: filteredUtrsData[0] })
        : undefined;
      return (
        <GridItemWrapper openHistoryModal={openHistoryModal} isEditing={false} item={item}>
          <MultiUtrsChartWidget
            readOnly={readOnly}
            item={item}
            utrsData={filteredUtrsData}
            initiativeId={initiativeId}
            survey={survey}
            handleSetTarget={handleSetTarget}
            altUnitText={altUnitText}
            currencyConfig={currencyConfig}
          />
          <MultiUtrsChartFooter
            item={item}
            actionButton={actionButton}
            questionReference={getQuestionReference('text-left')}
          />
        </GridItemWrapper>
      );
    }

    // this is used to support old charts, new charts should take data from calculation and variables
    return (
      <GridItemWrapper
        {...props}
        openHistoryModal={() => handleOpenUtrvHistoryModal({ item, utrData: filteredUtrsData[0] })}
      >
        <ChartWidget
          subType={item.subType}
          utrData={filteredUtrsData[0]}
          selectedColumnCode={Object.values(variables)[0].valueListCode}
          handleSetTarget={handleSetTarget}
          readOnly={readOnly}
          unitText={item.unitText}
          initiativeId={initiativeId}
          survey={survey}
          type={item.type}
          altUnitText={altUnitText}
          hideQuestionReference={hideQuestionReference}
          actionButton={actionButton}
          currencyConfig={currencyConfig}
        />
      </GridItemWrapper>
    );
  }

  if (item.type === InsightDashboardItemType.Integration) {
    const { variables, calculation } = item;
    const { utrsData = [] } = integrationsData || {};
    const { filteredUtrsData } = getFilteredUtrsData(utrsData, variables);
    const noData = filteredUtrsData?.every(({ utrvs }) => !utrvs.length);

    if (noData) {
      return (
        <GridItemWrapper isEditing={false} item={item} integrationsData={integrationsData}>
          {NoDataView ? <NoDataView item={item} {...dataFilters} /> : <NoData item={item} {...dataFilters} />}
        </GridItemWrapper>
      );
    }

    if (calculation) {
      return (
        <GridItemWrapper isEditing={false} item={item}>
          <MultiUtrsChartWidget
            readOnly={readOnly}
            item={item}
            utrsData={filteredUtrsData}
            initiativeId={initiativeId}
            survey={survey}
            altUnitText=''
          />
        </GridItemWrapper>
      );
    }

    return (
      <GridItemWrapper isEditing={false} item={item} integrationsData={integrationsData}>
        <SingleValueIntegration
          item={item}
          integrationData={integrationsData}
          hasSparkLine={item.subType === ChartSubType.SparkLine}
        />
      </GridItemWrapper>
    );
  }

  if (isTableType(item)) {
    const { variables, calculation, title, config, subType } = item;
    const { filteredUtrsData } = getFilteredUtrsData(utrsData as HistoricalUtrs[], variables);

    const openUtrvHistoryModal = (utrCode: string) => {
      const utrData = filteredUtrsData.find(({ utr }) => utr.code === utrCode);

      if (!utrData) {
        return;
      }
      handleOpenUtrvHistoryModal({ item, utrData });
    };

    if (subType === TableSubType.DirectTable) {
      return (
        <DirectTableWidget
          utrsData={filteredUtrsData}
          valueListMap={valueListMap}
          surveyUnitConfigMap={surveyUnitConfigMap}
          config={config}
          itemId={item._id}
        />
      );
    }

    return (
      <TableWidget
        title={title}
        utrsData={filteredUtrsData}
        variables={variables}
        calculation={calculation}
        onClickUtrValueLabel={openUtrvHistoryModal}
        config={config}
      />
    );
  }

  if (isMediaType(item)) {
    const { variables = {} } = item;
    const { filteredUtrsData } = getFilteredUtrsData(utrsData, variables);

    return <MediaWidget item={item} isEditing={isEditing} utrsData={filteredUtrsData} />;
  }

  if (isSDGContributionType(item) && scorecard) {
    return <SDGContributionWidget initiativeId={initiativeId} scorecard={scorecard} />;
  }

  if (isSpaceType(item)) {
    return <div className={`w-100 h-100 ${isEditing ? 'blank-space background-ThemeBgExtralight' : ''} `}></div>;
  }

  return null;
};
